import dayjs from 'dayjs';
import _ from 'lodash';
import { parseHint } from './hint-manager';
import { parseOptionDescription } from './option-manager';

function parseOrderDescription(order, showGroup = true) {
  const options =
    order.options?.reduce(
      (acc, option) => acc.concat(`, ${parseOptionDescription(option, false)}`),
      ''
    ) ?? '';
  const item = order.item.name != null ? order.item.name : '';

  let hint =
    order.price?.hint != null ? ` ${parseHint(order.price?.hint, true)}` : '';
  if (_.isEmpty(hint))
    hint =
      order?.item?.hint != null ? ` ${parseHint(order?.item?.hint, true)}` : '';

  return `${
    showGroup ? `${order.item.category.name}: ` : ''
  }${item}${options}${hint}`.replace(/' {2,}'/g, ' ');
}

function filterByUser(orders, user) {
  if (!_.isArray(orders)) return null;

  return orders?.filter((order) => order.bill.customer.id === user?.id);
}

function isCancelled(order) {
  return order?.cancelled_at != null;
}

function isSelectable(order) {
  return isPayable(order);
}

function isCancellable(order, user) {
  return (
    dayjs().diff(order.session?.expires_at) <= 0 &&
    order.bill?.customer?.id === user?.id &&
    order.cancelled_at == null
  );
}

function isPayable(order, { ignorePaying } = { ignorePaying: false }) {
  const statues = ['paid'];

  if (!ignorePaying) {
    statues.push('paying');
  }

  return !isCancelled(order) && !statues.includes(order?.payment?.status);
}

function parseOrdersQuantity(orders) {
  return orders?.reduce((acc, order) => acc + order.quantity, 0);
}

function parsePayableOrders(orders) {
  return orders?.filter((order) => isPayable(order));
}

function parseToDistinctOrders(orders) {
  const distinctOrders = [];

  orders?.forEach((order) => {
    const string = parseOrderDescription(order);
    const distinctOrder = distinctOrders.find(
      (obj) => parseOrderDescription(obj) === string
    );

    if (distinctOrder == null) {
      distinctOrders.push({ ...order, count: order.quantity });
    } else {
      distinctOrder.count += order.quantity;
    }
  });

  return distinctOrders;
}

function sortOrdersByCreation(orders) {
  let result = null;

  if (orders != null) {
    result = orders.sort((a, b) => {
      if (dayjs(a.created_at) > dayjs(b.created_at)) return 1;
      if (dayjs(a.created_at) < dayjs(b.created_at)) return -1;

      if (a.bill.customer.id > b.bill.customer.id) return 1;
      if (a.bill.customer.id < b.bill.customer.id) return -1;

      return 0;
    });
  }

  return result;
}

function sortOrdersByBillAndCreation(orders) {
  return orders?.sort((a, b) => {
    if (a.bill.customer.name > b.bill.customer.name) return 1;
    if (a.bill.customer.name < b.bill.customer.name) return -1;

    if (a.bill.customer.id > b.bill.customer.id) return 1;
    if (a.bill.customer.id < b.bill.customer.id) return -1;

    if (dayjs(a.created_at) > dayjs(b.created_at)) return 1;
    if (dayjs(a.created_at) < dayjs(b.created_at)) return -1;

    return 0;
  });
}

function parseTotal(orders) {
  return (
    orders?.reduce(
      (acum, order) => acum + (isPayable(order) ? order.value : 0),
      0
    ) ?? 0
  );
}

function parseUserTotal(orders, user) {
  const result =
    filterByUser(orders, user)?.reduce(
      (acum, order) =>
        acum + (isPayable(order, { ignorePaying: true }) ? order.value : 0),
      0
    ) ?? 0;

  return result;
}

function parseTip(orders, isTipEnabled, suggestion, verifyPayable = true) {
  const tip = isTipEnabled ? suggestion / 100 : 0;

  const value =
    orders?.reduce(
      (acum, order) =>
        acum +
        ((!verifyPayable || isPayable(order)) && !order.item?.non_tip
          ? order.value * tip
          : 0),
      0
    ) ?? 0;

  return _.round(value, 2);
}

function computePercent(tip, defaultTip, suggestionPercent) {
  let result = Math.round((tip / defaultTip) * suggestionPercent);

  if (_.isNaN(result) || !_.isFinite(result)) {
    result = 100;
  }

  return result;
}

export {
  computePercent,
  filterByUser,
  isCancellable,
  isCancelled,
  isPayable,
  isSelectable,
  parseOrderDescription,
  parsePayableOrders,
  parseTip,
  parseToDistinctOrders,
  parseTotal,
  parseUserTotal,
  sortOrdersByBillAndCreation,
  sortOrdersByCreation,
  parseOrdersQuantity,
};
